import {
  Box,
  Button,
  Card,
  HStack,
  Stack,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"

import { PageShell } from "@/components/Layout"
import {
  useApproveTopicProposal,
  useDismissTopicProposal,
  useTopicProposals,
} from "@/hooks/useTopicProposals"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

export function TopicProposalsPage() {
  const { data, isLoading } = useTopicProposals("proposed")
  const approve = useApproveTopicProposal()
  const dismiss = useDismissTopicProposal()
  const breadcrumbs = useRouteBreadcrumbs("admin-topic-proposals.index")

  if (isLoading) return <Text>Loading…</Text>
  const items = data?.data ?? []

  return (
    <PageShell
      title="Topic Proposals"
      description="Review suggested topic names before approving them into the shared topic library."
      breadcrumbs={breadcrumbs}
    >
      <Stack gap={4}>
        {items.length === 0 ? (
          <Text color="fg.muted">No proposals awaiting review.</Text>
        ) : (
          <>
            {/* Desktop */}
            <Box display={{ base: "none", md: "block" }}>
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Proposed name</Table.ColumnHeader>
                    <Table.ColumnHeader>Question</Table.ColumnHeader>
                    <Table.ColumnHeader>Actions</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {items.map((p) => (
                    <Table.Row key={p.id}>
                      <Table.Cell fontWeight="medium">
                        {p.proposed_topic_name}
                      </Table.Cell>
                      <Table.Cell maxW="400px" truncate>
                        {p.question_text}
                      </Table.Cell>
                      <Table.Cell>
                        <HStack>
                          <Button
                            size="xs"
                            colorPalette="green"
                            loading={approve.isPending}
                            onClick={() => approve.mutate(p.id)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            loading={dismiss.isPending}
                            onClick={() => dismiss.mutate(p.id)}
                          >
                            Dismiss
                          </Button>
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>

            {/* Mobile cards */}
            <VStack
              display={{ base: "flex", md: "none" }}
              gap={3}
              align="stretch"
            >
              {items.map((p) => (
                <Card.Root key={p.id}>
                  <Card.Body>
                    <Text fontWeight="medium">{p.proposed_topic_name}</Text>
                    <Text fontSize="sm" color="fg.muted" mt={1} lineClamp={2}>
                      {p.question_text}
                    </Text>
                    <HStack mt={3}>
                      <Button
                        size="sm"
                        colorPalette="green"
                        loading={approve.isPending}
                        onClick={() => approve.mutate(p.id)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        loading={dismiss.isPending}
                        onClick={() => dismiss.mutate(p.id)}
                      >
                        Dismiss
                      </Button>
                    </HStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </VStack>
          </>
        )}
      </Stack>
    </PageShell>
  )
}

export const Route = createFileRoute("/_layout/admin-topic-proposals/")({
  component: TopicProposalsPage,
})
